import React, { useState, useEffect } from 'react';
import { Save, Settings as SettingsIcon, Phone, Clock, Sun, Moon, Check } from 'lucide-react';
import apiService from '../services/apiService';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';
import { SCRIPT_LABEL_TR } from '../i18n/tr';

const THEME_OPTIONS = [
  {
    id: 'light',
    label: 'Açık tema',
    description: 'Beyaz zemin, yumuşak gri tonlar — aydınlık ortamlar için.',
    icon: Sun,
    swatch: ['#ffffff', '#f1f4f9', '#2d6459'],
  },
  {
    id: 'dark',
    label: 'Koyu tema',
    description: 'Lacivertimsi siyah zemin — düşük ışıkta göz yormaz.',
    icon: Moon,
    swatch: ['#111318', '#1a1d27', '#4a9e8a'],
  },
];

const Settings = () => {
  const { reload: reloadGlobalSettings } = useSettings();
  const { theme, setTheme } = useTheme();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    apiService.fetchSettings().then(setSettings).catch(console.error);
  }, []);

  const update = (path, value) => {
    setSettings((prev) => {
      const next = { ...prev };
      const keys = path.split('.');
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...cur[keys[i]] };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await apiService.saveSettings(settings);
      reloadGlobalSettings();
      setMessage('Ayarlar kaydedildi.');
    } catch (e) {
      setMessage(`Hata: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin h-8 w-8 border-b-2 border-accent-400 rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white flex items-center gap-2">
          <SettingsIcon className="w-8 h-8 text-accent-300" />
          Randevu & Hat Ayarları
        </h1>
        <p className="mt-2 text-slate-400">
          Randevu saatleri, kapasite, karşılama metni ve yönlendirme numaralarını buradan yönetin.
          Değişiklikler sesli asistan oturumlarında kullanılır.
        </p>
      </div>

      <div className="space-y-6">
        <section className="card">
          <h2 className="text-lg font-semibold mb-1 flex items-center gap-2 text-white">
            <Sun className="w-5 h-5 text-accent-300" /> Görünüm
          </h2>
          <p className="mb-4 text-sm text-slate-400">
            Panelin renk temasını seçin. Tercihiniz bu cihazda kaydedilir ve tüm sayfalarda
            sağ üstteki tuştan da değiştirilebilir.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {THEME_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const active = theme === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setTheme(opt.id)}
                  aria-pressed={active}
                  className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${
                    active
                      ? 'border-accent-400/60 bg-accent-400/10'
                      : 'border-white/10 hover:border-white/20 hover:bg-white/[0.04]'
                  }`}
                >
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] text-accent-300">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="font-semibold text-white">{opt.label}</span>
                      {active && (
                        <Check className="h-4 w-4 text-accent-300" />
                      )}
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-400">{opt.description}</span>
                    <span className="mt-2 flex gap-1.5">
                      {opt.swatch.map((c) => (
                        <span
                          key={c}
                          className="h-4 w-6 rounded-md ring-1 ring-black/10"
                          style={{ background: c }}
                        />
                      ))}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="card">
          <h2 className="text-lg font-semibold mb-4 text-white">Karşılama metni</h2>
          <textarea
            className="field w-full p-3"
            rows={4}
            value={settings.greeting}
            onChange={(e) => update('greeting', e.target.value)}
          />
        </section>

        <section className="card">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-white">
            <Clock className="w-5 h-5 text-accent-300" /> Çalışma saatleri & kapasite
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="text-sm">
              Başlangıç
              <input
                type="time"
                className="field mt-1 w-full"
                value={settings.workingHours.start}
                onChange={(e) => update('workingHours.start', e.target.value)}
              />
            </label>
            <label className="text-sm">
              Bitiş
              <input
                type="time"
                className="field mt-1 w-full"
                value={settings.workingHours.end}
                onChange={(e) => update('workingHours.end', e.target.value)}
              />
            </label>
            <label className="text-sm">
              Slot süresi (dk)
              <input
                type="number"
                min={15}
                step={15}
                className="field mt-1 w-full"
                value={settings.slotDurationMinutes}
                onChange={(e) => update('slotDurationMinutes', Number(e.target.value))}
              />
            </label>
            <label className="text-sm">
              Öğleden sonra eşiği (hatırlatma metni için)
              <input
                type="time"
                className="field mt-1 w-full"
                value={settings.afternoonCutoff}
                onChange={(e) => update('afternoonCutoff', e.target.value)}
              />
            </label>
          </div>
        </section>

        <section className="card">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-white">
            <Phone className="w-5 h-5 text-accent-300" /> Yönlendirme hedefleri (MTS / SKS / Danışman)
          </h2>
          {settings.transfer.targets.map((t, i) => (
            <div key={t.id} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3 pb-3 border-b border-white/10">
              <input
                className="field"
                value={t.label}
                onChange={(e) => {
                  const targets = [...settings.transfer.targets];
                  targets[i] = { ...t, label: e.target.value };
                  update('transfer.targets', targets);
                }}
              />
              <input
                className="field"
                value={t.phone}
                onChange={(e) => {
                  const targets = [...settings.transfer.targets];
                  targets[i] = { ...t, phone: e.target.value };
                  update('transfer.targets', targets);
                }}
                placeholder="+90..."
              />
              <input
                className="field md:col-span-1"
                value={t.description}
                onChange={(e) => {
                  const targets = [...settings.transfer.targets];
                  targets[i] = { ...t, description: e.target.value };
                  update('transfer.targets', targets);
                }}
              />
            </div>
          ))}
          <label className="flex items-center gap-2 text-sm mt-4">
            <input
              type="checkbox"
              checked={settings.transfer.requireAnswerBeforeTransfer}
              onChange={(e) => update('transfer.requireAnswerBeforeTransfer', e.target.checked)}
            />
            Aktarmadan önce karşı tarafın cevap vermesini zorunlu kıl (sıcak aktarım)
          </label>
        </section>

        <section className="card">
          <h2 className="text-lg font-semibold mb-4 text-white">Konuşma scriptleri</h2>
          {Object.entries(settings.scripts).map(([key, text]) => (
            <label key={key} className="block text-sm mb-4">
              <span className="font-medium text-slate-300">{SCRIPT_LABEL_TR[key] || key}</span>
              <textarea
                className="field mt-1 w-full p-2"
                rows={2}
                value={text}
                onChange={(e) => update(`scripts.${key}`, e.target.value)}
              />
            </label>
          ))}
        </section>

        <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
          <Save className="w-4 h-4" />
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
        {message && <p className="text-sm text-slate-400">{message}</p>}
      </div>
    </div>
  );
};

export default Settings;
